import {
  type CollectionReference,
  collection,
  type DocumentReference,
  deleteDoc,
  doc,
  getDocs,
  onSnapshot,
  query,
  setDoc,
  where,
} from 'firebase/firestore';
import { firestore } from '../services/firebase';
import httpClient from './http/httpClient';

export interface LibraryFileMetadata {
  id: string; // Document ID
  name: string;
  size: number;
  uploadedAt: string;
  originalFilename?: string;
  type: string; // 'cvs', 'pastProposals', 'caseStudiesReports'
  storagePath?: string; // Path in storage bucket
  tokenCount?: number; // Estimated token count
  lastModified?: number;
  scope: 'user' | 'client' | 'team';
  level?: 'personal' | 'team' | 'company';
  isHidden?: boolean;
  isRequired?: boolean;
  uploaderName?: string;
  uploaderId?: string;
  conversionStatus?: string;
  convertedTxtFilename?: string;
  lastConversionSuccessTimestamp?: unknown;
  domain?: 'grant' | 'tender' | 'both';
  kmsMigrationFailed?: boolean;
  kmsMigrationAttemptedAt?: string;
}

/**
 * Uploads a library file to the backend storage service
 */
export async function uploadLibraryFile({
  userId,
  clientId,
  actualBucketName,
  userFolderPath,
  file,
  type,
  scope = 'user',
  level,
  isHidden,
  isRequired,
}: {
  userId: string;
  clientId: string;
  actualBucketName: string;
  userFolderPath: string; // Only used if scope is 'user'
  file: File;
  type: string;
  scope?: 'user' | 'client' | 'team';
  level?: 'personal' | 'team' | 'company';
  isHidden?: boolean;
  isRequired?: boolean;
}) {
  // Validate inputs
  if (!userId || !clientId || !actualBucketName || !file || !type) {
    throw new Error('Missing required parameters for library file upload.');
  }

  try {
    const keyHeader = '';

    const headers: Record<string, string> = {
      'Content-Type': 'application/octet-stream',
      'X-User-ID': userId,
      'X-Client-ID': clientId,
      'X-Bucket-Name': actualBucketName,
      'X-Folder-Path': userFolderPath, // Kept for backward compatibility/user scope
      'X-File-Type': type,
      'X-File-Name': file.name,
      'X-Scope': scope,
      'X-File-Mime-Type': file.type || 'application/octet-stream',
    };

    if (level) {
      headers['X-Level'] = level;
    }
    if (isHidden !== undefined) {
      headers['X-Is-Hidden'] = isHidden.toString();
    }
    if (isRequired !== undefined) {
      headers['X-Is-Required'] = isRequired.toString();
    }

    // Always encrypt if key is available
    if (keyHeader) {
      headers['X-Encryption-Key'] = keyHeader;
    }

    // Send file as binary blob with metadata in headers
    const response = await httpClient.post<{
      success: boolean;
      filePath?: string;
      storagePath?: string;
      message?: string;
    }>('/uploadLibraryFile', file, {
      headers,
    });

    if (response?.success) {
      return response.storagePath || response.filePath || response.message;
    } else {
      throw new Error(response.message || 'Library file upload failed.');
    }
  } catch (error: unknown) {
    const err = error as Error;
    throw new Error(`Library file upload failed: ${err.message}`);
  }
}

/**
 * Saves the file metadata to Firestore under users/{userId}/library or clients/{clientId}/library
 */
export async function saveLibraryFileMetadata({
  userId,
  clientId,
  teamId,
  file,
  type,
  scope = 'user',
  storagePath,
}: {
  userId: string;
  clientId?: string;
  teamId?: string;
  file: File;
  type: string;
  scope?: 'user' | 'client' | 'team';
  storagePath?: string;
}) {
  if (!userId || !file || !type) {
    throw new Error('Missing metadata params');
  }

  if (scope === 'client' && !clientId) {
    throw new Error('Missing clientId for client scope metadata');
  }

  if (scope === 'team' && !teamId) {
    throw new Error('Missing teamId for team scope metadata');
  }

  // Get filename without extension for document ID
  const lastDotIndex = file.name.lastIndexOf('.');
  const docId = lastDotIndex === -1 ? file.name : file.name.substring(0, lastDotIndex);

  let fileDocRef: DocumentReference;
  if (scope === 'client' && clientId) {
    fileDocRef = doc(firestore, 'clients', clientId, 'library', docId);
  } else if (scope === 'team' && teamId) {
    fileDocRef = doc(firestore, 'teams', teamId, 'library', docId);
  } else {
    fileDocRef = doc(firestore, 'users', userId, 'library', docId);
  }

  const fileMeta = {
    originalFilename: file.name,
    size: file.size,
    uploadedAt: new Date().toISOString().split('T')[0], // YYYY-MM-DD
    type: type,
    storagePath: storagePath || '', // Ensure storagePath is saved
    tokenCount: 0, // Default placeholder
    lastModified: file.lastModified,
  };
  await setDoc(fileDocRef, fileMeta, { merge: true });
}

/**
 * Fetches the file list (with metadata) from Firestore for the given user (Unified Library)
 */
export async function fetchLibraryFileList({
  userId,
  type, // Optional filter
}: {
  userId: string;
  type?: string;
}): Promise<LibraryFileMetadata[]> {
  if (!userId) {
    throw new Error('Missing fetch params');
  }

  const colRef = collection(firestore, 'users', userId, 'library');
  let q = query(colRef);

  if (type) {
    q = query(colRef, where('type', '==', type));
  }

  const querySnapshot = await getDocs(q);
  return querySnapshot.docs.map((docSnap) => {
    const data = docSnap.data();
    return {
      id: docSnap.id,
      ...data,
      uploadedAt: normalizeDate(data.uploadedAt),
      scope: 'user',
    };
  }) as LibraryFileMetadata[];
}

/**
 * Fetches the client file list (with metadata) from Firestore for the given client (Unified Library)
 */
export async function fetchClientLibraryFileList({
  clientId,
  type, // Optional filter
}: {
  clientId: string;
  type?: string;
}): Promise<LibraryFileMetadata[]> {
  if (!clientId) {
    throw new Error('Missing fetch params');
  }

  const colRef = collection(firestore, 'clients', clientId, 'library');
  let q = query(colRef);

  if (type) {
    q = query(colRef, where('type', '==', type));
  }

  const querySnapshot = await getDocs(q);
  return querySnapshot.docs.map((docSnap) => {
    const data = docSnap.data();
    return {
      id: docSnap.id,
      ...data,
      uploadedAt: normalizeDate(data.uploadedAt),
      scope: 'client',
    };
  }) as LibraryFileMetadata[];
}

/**
 * Subscribes to the file list (with metadata) from Firestore for the given user/client
 */
export function subscribeToLibraryFiles({
  userId,
  clientId,
  teamId,
  type, // Optional filter
  onUpdate,
  onError,
  scope = 'user',
}: {
  userId: string;
  clientId?: string;
  teamId?: string;
  type?: string;
  onUpdate: (files: LibraryFileMetadata[]) => void;
  onError?: (error: Error) => void;
  scope?: 'user' | 'client' | 'team';
}) {
  if (!userId) {
    throw new Error('Missing subscription params');
  }

  if (scope === 'client' && !clientId) {
    throw new Error('Missing clientId for client scope subscription');
  }

  if (scope === 'team' && !teamId) {
    throw new Error('Missing teamId for team scope subscription');
  }

  let colRef: CollectionReference;
  if (scope === 'client' && clientId) {
    colRef = collection(firestore, 'clients', clientId, 'library');
  } else if (scope === 'team' && teamId) {
    colRef = collection(firestore, 'teams', teamId, 'library');
  } else {
    colRef = collection(firestore, 'users', userId, 'library');
  }

  let q = query(colRef);
  if (type) {
    q = query(colRef, where('type', '==', type));
  }

  return onSnapshot(
    q,
    (querySnapshot) => {
      const arr = querySnapshot.docs.map((docSnap) => {
        const data = docSnap.data();
        return {
          id: docSnap.id,
          ...data,
          uploadedAt: normalizeDate(data.uploadedAt),
          scope: scope,
        };
      });
      onUpdate(arr as LibraryFileMetadata[]);
    },
    (error) => {
      if (onError) {
        onError(error);
      }
    }
  );
}

/**
 * Updates properties of a library file
 */
export async function updateLibraryFileProperties({
  userId,
  clientId,
  teamId,
  docId,
  updates,
  scope = 'user',
}: {
  userId: string;
  clientId?: string;
  teamId?: string;
  docId: string;
  updates: Partial<LibraryFileMetadata>;
  scope?: 'user' | 'client' | 'team';
}) {
  if (!userId || !docId) {
    throw new Error('Missing update params');
  }

  let docRef: DocumentReference;
  if (scope === 'client' && clientId) {
    docRef = doc(firestore, 'clients', clientId, 'library', docId);
  } else if (scope === 'team' && teamId) {
    docRef = doc(firestore, 'teams', teamId, 'library', docId);
  } else {
    docRef = doc(firestore, 'users', userId, 'library', docId);
  }

  await setDoc(docRef, updates, { merge: true });
}

/**
 * Updates the type of a library file
 */
export async function updateLibraryFileType({
  userId,
  clientId,
  teamId,
  docId,
  newType,
  scope = 'user',
}: {
  userId: string;
  clientId?: string;
  teamId?: string;
  docId: string;
  newType: string;
  scope?: 'user' | 'client' | 'team';
}) {
  if (!userId || !docId || !newType) {
    throw new Error('Missing update params');
  }

  let docRef: DocumentReference;
  if (scope === 'client' && clientId) {
    docRef = doc(firestore, 'clients', clientId, 'library', docId);
  } else if (scope === 'team' && teamId) {
    docRef = doc(firestore, 'teams', teamId, 'library', docId);
  } else {
    docRef = doc(firestore, 'users', userId, 'library', docId);
  }

  await setDoc(docRef, { type: newType }, { merge: true });
}

/**
 * Deletes a file from storage and its metadata from Firestore using storage abstraction.
 */
export async function deleteLibraryFile({
  userId,
  clientId,
  teamId,
  docId,
  fileName,
  type, // Kept for API signature compatibility, but we should rely on storagePath if available
  userFolderPath,
  actualBucketName,
  scope = 'user',
  storagePath, // If known, pass it to avoid lookup or guessing
}: {
  userId: string;
  clientId: string;
  teamId?: string;
  docId: string;
  fileName: string;
  type?: string;
  userFolderPath: string;
  actualBucketName: string;
  scope?: 'user' | 'client' | 'team';
  storagePath?: string;
}) {
  if (!userId || !clientId || !fileName || !actualBucketName) {
    throw new Error('deleteLibraryFile: Missing required parameters');
  }

  // If storagePath is not provided, we might need to guess it based on old logic or fetch it.
  // The UI should ideally pass the storagePath from the document metadata.
  // If we only have type, we use the old logic.

  if (!storagePath && type) {
    // Reconstruct old path if needed, but ideally we use storagePath
    // This fallback logic might need to be robust.
  }

  try {
    // Call the backend deleteLibraryFile function which uses storage abstraction
    const response = await httpClient.post<{ success: boolean; message?: string }>(
      '/deleteLibraryFile',
      {
        userId,
        clientId,
        type: type || 'unknown', // Backend might still expect this for validation, or we update backend too?
        fileName,
        userFolderPath,
        actualBucketName,
        scope,
        storagePath, // Pass the explicit path if we have it
        docId, // Pass precise docId to avoid computing it incorrectly
      }
    );

    if (!response?.success) {
      throw new Error(response?.message || 'Delete failed.');
    }

    // Delete Firestore document from the library collection
    let docRef: DocumentReference;
    if (scope === 'client') {
      docRef = doc(firestore, 'clients', clientId, 'library', docId);
    } else if (scope === 'team' && teamId) {
      docRef = doc(firestore, 'teams', teamId, 'library', docId);
    } else {
      docRef = doc(firestore, 'users', userId, 'library', docId);
    }

    // We also need to check if it exists in the OLD collection and delete it there too if we are in transition?
    // No, assuming migration is done.

    await deleteDoc(docRef);
  } catch (error: unknown) {
    const err = error as Error;
    throw new Error(`Library file deletion failed: ${err.message}`);
  }
}

// Helper to normalize Firestore timestamps to YYYY-MM-DD strings
function normalizeDate(date: unknown): string {
  if (!date) {
    return '';
  }
  if (typeof date === 'string') {
    return date;
  }

  const d = date as { toDate?: () => Date; seconds?: number };

  // Handle Firestore Timestamp (has toDate())
  if (d && typeof d.toDate === 'function') {
    return d.toDate().toISOString().split('T')[0] || '';
  }
  // Handle raw object { seconds, nanoseconds }
  if (d && typeof d.seconds === 'number') {
    return new Date(d.seconds * 1000).toISOString().split('T')[0] || '';
  }
  return String(date);
}

/**
 * Calls the backend to move a document to a different level (personal, team, company)
 */
export async function moveDocumentLevel({
  docId,
  sourceLevel,
  targetLevel,
  userId,
  clientId,
  teamId,
  actualBucketName,
}: {
  docId: string;
  sourceLevel: 'personal' | 'team' | 'company';
  targetLevel: 'personal' | 'team' | 'company';
  userId: string;
  clientId: string;
  teamId?: string | undefined;
  actualBucketName: string;
}) {
  if (!docId || !sourceLevel || !targetLevel || !userId || !clientId || !actualBucketName) {
    throw new Error('Missing required parameters for moving document.');
  }

  try {
    const response = await httpClient.post<{ success: boolean; message?: string }>(
      '/moveDocumentLevel',
      {
        docId,
        sourceLevel,
        targetLevel,
        userId,
        clientId,
        teamId,
        actualBucketName,
      }
    );

    if (!response?.success) {
      throw new Error(response?.message || 'Move failed.');
    }
    return response;
  } catch (error: unknown) {
    const err = error as Error;
    throw new Error(`Failed to move document: ${err.message}`);
  }
}
